'use client';

/**
 * Video Tracking Hook
 *
 * Tracks video playback analytics using the shared analytics service.
 * Supports video_start, video_progress milestones, video_pause, and video_exit.
 */

import { useRef, useCallback, useEffect } from 'react';
import { analyticsService } from '../services/AnalyticsService';
import { ANALYTICS_EVENT_NAMES } from '../utils/analytics/constants';
import type { VideoAnalyticsConfig } from '../utils/analytics/videoHelpers';

const VIDEO_COMPONENT_FAMILY = 'video';
const VIDEO_COMPONENT_TYPE = 'embedded-player';

export interface VideoTrackingConfig extends VideoAnalyticsConfig {
  moduleName: string;
}

interface VideoTrackingState {
  hasStarted: boolean;
  isPaused: boolean;
  currentTime: number;
  duration: number;
  milestones: Set<number>;
}

export function useVideoTracking(config: VideoTrackingConfig | null) {
  const stateRef = useRef<VideoTrackingState>({
    hasStarted: false,
    isPaused: false,
    currentTime: 0,
    duration: 0,
    milestones: new Set(),
  });

  useEffect(() => {
    stateRef.current = {
      hasStarted: false,
      isPaused: false,
      currentTime: 0,
      duration: 0,
      milestones: new Set(),
    };
  }, [config]);

  const trackVideoStart = useCallback(
    (duration: number) => {
      if (!config || stateRef.current.hasStarted) return;

      stateRef.current.hasStarted = true;
      stateRef.current.duration = duration;

      analyticsService.trackEvent({
        event: ANALYTICS_EVENT_NAMES.VIDEO_START,
        module_name: config.moduleName,
        component_family: VIDEO_COMPONENT_FAMILY,
        component_type: VIDEO_COMPONENT_TYPE,
        section_header: config.sectionHeader,
        video_id: config.videoId,
        video_title: config.videoTitle.toLowerCase(),
        video_url: config.videoUrl.toLowerCase(),
        video_provider: config.videoProvider,
        video_duration: duration,
        autoplay: true,
      });
    },
    [config]
  );

  const trackVideoProgress = useCallback(
    (currentTime: number, duration: number) => {
      if (!config || !duration) return;

      stateRef.current.currentTime = currentTime;
      stateRef.current.duration = duration;

      const progressPercent = (currentTime / duration) * 100;
      const milestones: Array<25 | 50 | 75 | 100> = [25, 50, 75, 100];

      for (const milestone of milestones) {
        if (progressPercent >= milestone && !stateRef.current.milestones.has(milestone)) {
          stateRef.current.milestones.add(milestone);

          analyticsService.trackEvent({
            event: ANALYTICS_EVENT_NAMES.VIDEO_PROGRESS,
            module_name: config.moduleName,
            component_family: VIDEO_COMPONENT_FAMILY,
            component_type: VIDEO_COMPONENT_TYPE,
            section_header: config.sectionHeader,
            video_id: config.videoId,
            video_title: config.videoTitle.toLowerCase(),
            video_url: config.videoUrl.toLowerCase(),
            video_provider: config.videoProvider,
            progress_milestone: milestone,
            current_time: Math.floor(currentTime),
            video_duration: duration,
          });
        }
      }
    },
    [config]
  );

  const trackVideoPause = useCallback(
    (currentTime: number, duration: number) => {
      if (!config || stateRef.current.isPaused) return;

      stateRef.current.isPaused = true;
      stateRef.current.currentTime = currentTime;

      analyticsService.trackEvent({
        event: ANALYTICS_EVENT_NAMES.VIDEO_PAUSE,
        module_name: config.moduleName,
        component_family: VIDEO_COMPONENT_FAMILY,
        component_type: VIDEO_COMPONENT_TYPE,
        section_header: config.sectionHeader,
        video_id: config.videoId,
        video_title: config.videoTitle.toLowerCase(),
        video_url: config.videoUrl.toLowerCase(),
        video_provider: config.videoProvider,
        current_time: Math.floor(currentTime),
        video_duration: duration,
        watch_time: Math.floor(currentTime),
      });
    },
    [config]
  );

  const trackVideoExit = useCallback(
    (exitMethod: 'close_button' | 'escape' | 'backdrop' = 'close_button') => {
      if (!config) return;

      const { currentTime, duration, milestones } = stateRef.current;
      const videoCompleted = milestones.has(100);
      const completionRate = duration > 0 ? Math.floor((currentTime / duration) * 100) : 0;

      analyticsService.trackEvent({
        event: ANALYTICS_EVENT_NAMES.VIDEO_EXIT,
        module_name: config.moduleName,
        component_family: VIDEO_COMPONENT_FAMILY,
        component_type: VIDEO_COMPONENT_TYPE,
        section_header: config.sectionHeader,
        video_id: config.videoId,
        video_title: config.videoTitle.toLowerCase(),
        video_url: config.videoUrl.toLowerCase(),
        video_provider: config.videoProvider,
        video_completed: videoCompleted,
        watch_time: Math.floor(currentTime),
        video_duration: duration,
        completion_rate: completionRate,
        exit_method: exitMethod,
      });
    },
    [config]
  );

  const markVideoResumed = useCallback(() => {
    stateRef.current.isPaused = false;
  }, []);

  return {
    trackVideoStart,
    trackVideoProgress,
    trackVideoPause,
    trackVideoExit,
    markVideoResumed,
  };
}
